import { API, getRequest } from '@/api/api';

import { mapVessel, mapVesselsPage } from './vessels.mappers';
import type {
  GetOwnedVesselsParams,
  GetVesselParams,
  GetVesselsByIdsParams,
  Vessel,
  VesselDto,
  VesselsPage,
  VesselsPageDto,
} from './vessels.types';

/**
 * GET /{id} (vessel service) → one vessel, whatever the caller's role on it. Answers the
 * same fields as a list row, unwrapped.
 *
 * A vessel is its own service, so its id doubles as the `X-Service-Id` the role middleware
 * demands — which is also what makes the check meaningful: it asks whether the caller holds
 * a role on the very vessel being read. Someone else's vessel is refused, not disclosed:
 * as a 404 from the handler, or as a role rejection from the middleware ahead of it.
 */
export const getVesselById = async ({ id, serviceId }: GetVesselParams): Promise<Vessel> => {
  const { data } = await getRequest<VesselDto>(API.VESSEL, `/${id}`, false, undefined, serviceId);
  return mapVessel(data);
};

/**
 * GET / (vessel service) filtered by `owner_id` → the vessels this person owns, as
 * opposed to the ones they merely have access to.
 */
export const getOwnedVessels = async ({
  ownerId,
  serviceId,
  limit = 50,
  page = 1,
}: GetOwnedVesselsParams): Promise<VesselsPage> => {
  const { data } = await getRequest<VesselsPageDto>(
    API.VESSEL,
    '',
    false,
    { owner_id: ownerId, limit, page },
    serviceId,
  );
  return mapVesselsPage(data);
};

/**
 * GET / (vessel service) filtered by `ids` → the named vessels, whatever the caller's role on
 * each. The filter is not narrowed to the header's service, so one request covers vessels the
 * caller captains alongside ones they own; the header only satisfies the permission middleware,
 * which asks for `GetVesselInfo` on that one service.
 *
 * The page is sized to the request so nothing asked for is paged away.
 */
export const getVesselsByIds = async ({
  ids,
  serviceId,
}: GetVesselsByIdsParams): Promise<VesselsPage> => {
  if (!ids.length) return { count: 0, items: [] };

  const { data } = await getRequest<VesselsPageDto>(
    API.VESSEL,
    '',
    false,
    { ids, limit: ids.length, page: 1 },
    serviceId,
  );
  return mapVesselsPage(data);
};
